import { useQuery } from "@tanstack/react-query";
import { Table, Tooltip } from "antd";
import React from "react";
import { useParams } from "react-router-dom";
import useInfraFormData from "../../../components/hooks/useInfraFormData";
import useProjectInfoQuery from "../../../components/hooks/useProjectInfoQuery";
import useProjectSpecificFilesList from "../../../components/hooks/useProjectSpecificFilesList";
import useProjectWiseClientList from "../../../components/hooks/useProjectWiseClientList";
import {
  clientProgressDataWithStatus,
  currentRole,
  getProjectId,
  getProjectInfoById,
  scriptPresentHeaderStatus,
} from "./../../../api/apiCallManager";
import ClientEda from "./header-status/ClientEda";
import CombineData from "./header-status/CombineData";
import FinalData from "./header-status/FinalData";
import FinalEda from "./header-status/FinalEda";
import FinalModel from "./header-status/FinalModel";
import InitialDataQc from "./header-status/InitialDataQc";
import TransformScript from "./header-status/TransformScript";
import ClientEdaCel from "./row-staus/ClientEdaCel";
import CombineDataCel from "./row-staus/CombineDataCel";
import FinalDataCel from "./row-staus/FinalDataCel";
import FinalEdaCel from "./row-staus/FinalEdaCel";
import FinalModelCel from "./row-staus/FinalModelCel";
import InitialDataQcCel from "./row-staus/InitialDataQcCel";
import TransformScriptCell from "./row-staus/TransformScriptCell";
import AccessTo from "../../../components/is-show/AccessTo";
import { FL, PA } from "../../router/AddRoutesJson";

const DataStatusTable = () => {
  const { id } = useParams();
  const {
    data: infraFormData,
    error: infraFormError,
    isLoading: infraFormLoading,
  } = useInfraFormData(id);
  const {
    data: projectInfoData,
    error: projectInfoErro,
    isLoading: projectInfoLoading,
  } = useProjectInfoQuery(id, getProjectInfoById);
  // const { data: githubFormData } = useGithubConnectionFormData();
  const { data } = useProjectSpecificFilesList();
  const {
    data: clientData,
    isLoading: isClientDataIsLoading,
    isFetching: isClientDataIsFetching,
  } = useProjectWiseClientList();
  const { data: clientProgressData } = useQuery({
    queryKey: ["clientProgressDataWithStatus_clientProgressDataWithStatus"],
    queryFn: () =>
      clientProgressDataWithStatus(
        infraFormData[0]?.project_key || projectInfoData?.project_key
      ),
  });
  const currentProjectGitConnectionInfo = infraFormData?.find(
    (item) => item?.projectId === getProjectId()
  );

  const transformedClientData = clientData?.map((client, index) => {
    // Filter file list and infra list by matching clientName
    const matchingFiles = data?.filter(
      (file) => file.clientName === client.clientName
    );
    const matchingInfra = infraFormData?.filter(
      (infra) => infra.clientName === client.clientName
    );

    // Add fileList and infra fields to the client object
    return {
      ...client,
      key: index + 2,
      fileList: matchingFiles?.length ? matchingFiles : [], // If no match, add an empty array
      infra: matchingInfra?.length ? matchingInfra : [], // If no match, add an empty array
    };
  });
  const dataSource = transformedClientData?.flatMap((item) => {
    if (item.siteManager.length > 1) {
      return item.siteManager.map((siteData, index) => ({
        ...item,
        siteManager: siteData,
        key: `${item.key}-${index * 2}`, // Updating key for each siteManager
      }));
    } else {
      return {
        ...item,
        siteManager: item.siteManager[0],
        key: `${item.key}-1`, // Single siteManager case
      };
    }
  });
  if (clientProgressData?.length) {
    dataSource?.forEach((item) => {
      // Match the siteName from clientProgressData
      const matchingStatus = clientProgressData?.find(
        (status) => status?.site_name === item.siteManager.siteName
      );
      // If a match is found, assign celStatus
      if (matchingStatus) {
        item.celStatus = matchingStatus;
      }
    });
  }
  const isScriptPresentPayload = {
    event_type: "file_search",
    repoUrl: currentProjectGitConnectionInfo?.reponame,
    accessToken: currentProjectGitConnectionInfo?.accesstoken,
    file_name: [
      "initial_data_qc.py",
      "combine_data.py",
      "client_eda.py",
      "data_transformation.py",
      "final_data_qc.py",
      "final_eda.py",
      "final_model_qc.py",
    ],
    branch: currentProjectGitConnectionInfo?.branch,
  };
  const { data: scriptPresentData, isLoading: scriptPresentDataIsLoading } =
    useQuery({
      queryKey: ["scriptStatus_fileSearch"],
      queryFn: () => scriptPresentHeaderStatus(isScriptPresentPayload),
      enabled: !!isScriptPresentPayload.repoUrl,
    });

  const columns = [
    {
      title: "Clients",
      dataIndex: "clientName",
      key: "clientName",
      render: (text, record, index) => {
        const prevRow = dataSource[index - 1];
        if (prevRow && prevRow.clientName === record.clientName) {
          return {
            children: text,
            props: { rowSpan: 0 }, // hide duplicate rows
          };
        }
        const rowSpan = dataSource.filter(
          (item) => item.clientName === record.clientName
        ).length;
        return {
          children: text,
          props: { rowSpan }, // span across matching rows
        };
      },
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      render: (text, record, index) => {
        const prevRow = dataSource[index - 1];
        if (prevRow && prevRow.clientName === record.clientName) {
          return {
            children: text,
            props: { rowSpan: 0 },
          };
        }
        const rowSpan = dataSource.filter(
          (item) => item.clientName === record.clientName
        ).length;
        return {
          children: text,
          props: { rowSpan },
        };
      },
    },
    {
      title: "Site Name",
      render: (text, record) => (
        <div>
          <Tooltip placement="top" title={record.siteManager.email}>
            <div>{record.siteManager.siteName}</div>
          </Tooltip>
        </div>
      ),
      key: "siteManager",
    },
    {
      title: (
        <InitialDataQc
          scriptPresentData={scriptPresentData}
          scriptPresentDataIsLoading={scriptPresentDataIsLoading}
        />
      ),
      className: "text-center",
      render: (_, rowItems) => <InitialDataQcCel rowItems={rowItems} />,
      key: "InitialDataQc",
    },
    {
      title: (
        <CombineData
          scriptPresentData={scriptPresentData}
          scriptPresentDataIsLoading={scriptPresentDataIsLoading}
        />
      ),
      className: "text-center",
      render: (text, record, index) => {
        const prevRow = dataSource[index - 1];
        if (prevRow && prevRow.clientName === record.clientName) {
          return {
            children: (
              <CombineDataCel
                clientProgressData={clientProgressData}
                rowItems={record}
              />
            ),
            props: { rowSpan: 0 },
          };
        }
        const rowSpan = dataSource.filter(
          (item) => item.clientName === record.clientName
        ).length;
        return {
          children: (
            <CombineDataCel clientProgressData={clientData} rowItems={record} />
          ),
          props: { rowSpan },
        };
      },
      // render: (text, record, index) => {
      //   if (index === 0) {
      //     return {
      //       children: <CombineDataCel rowItems={record} />,
      //       props: { rowSpan: 2 },
      //     };
      //   }
      //   return {
      //     props: { rowSpan: 0 },
      //   };
      // },
      key: "CombineData",
      // key: `CombineData_${reRender}`,
    },
    {
      title: (
        <ClientEda
          scriptPresentData={scriptPresentData}
          scriptPresentDataIsLoading={scriptPresentDataIsLoading}
        />
      ),
      render: (text, record, index) => {
        const prevRow = dataSource[index - 1];
        if (prevRow && prevRow.clientName === record.clientName) {
          return {
            children: <ClientEdaCel rowItems={record} />,
            props: { rowSpan: 0 },
          };
        }
        const rowSpan = dataSource.filter(
          (item) => item.clientName === record.clientName
        ).length;
        return {
          children: <ClientEdaCel rowItems={record} />,
          props: { rowSpan },
        };
      },
      key: "cleda",
    },
    {
      title: (
        <TransformScript
          scriptPresentData={scriptPresentData}
          scriptPresentDataIsLoading={scriptPresentDataIsLoading}
        />
      ),
      className: "text-center",
      render: (text, record, index) => {
        const prevRow = dataSource[index - 1];
        if (prevRow && prevRow.clientName === record.clientName) {
          return {
            children: <TransformScriptCell rowItems={record} />,
            props: { rowSpan: 0 },
          };
        }
        const rowSpan = dataSource.filter(
          (item) => item.clientName === record.clientName
        ).length;
        return {
          children: <TransformScriptCell rowItems={record} />,
          props: { rowSpan },
        };
      },
      key: "TransformScriptCell",
    },
    // {
    //   title: <FinalDataModel />,
    //   className: "text-center",
    //   render: (text, record, index) => {
    //     if (index === 0) {
    //       return {
    //         children: <FinalDataModelCell />,
    //         props: { rowSpan: dataSource.length },
    //       };
    //     }
    //     return {
    //       props: { rowSpan: 0 },
    //     };
    //   },
    //   key: "CombisssneData",
    // },
    {
      title: (
        <FinalData
          scriptPresentData={scriptPresentData}
          scriptPresentDataIsLoading={scriptPresentDataIsLoading}
        />
      ),
      render: (text, record, index) => {
        const prevRow = dataSource[index - 1];
        if (prevRow && prevRow.clientName === record.clientName) {
          return {
            children: (
              <FinalDataCel
                rowItems={record}
                allTableData={transformedClientData}
              />
            ),
            props: { rowSpan: 0 },
          };
        }
        const rowSpan = dataSource.filter(
          (item) => item.clientName === record.clientName
        ).length;
        return {
          children: (
            <FinalDataCel
              rowItems={record}
              allTableData={transformedClientData}
            />
          ),
          props: { rowSpan },
        };
      },
      key: "qc3dsaFinal_Data",
    },
    {
      title: (
        <FinalModel
          scriptPresentData={scriptPresentData}
          scriptPresentDataIsLoading={scriptPresentDataIsLoading}
        />
      ),
      render: (text, record, index) => {
        const prevRow = dataSource[index - 1];
        if (prevRow && prevRow.clientName === record.clientName) {
          return {
            children: (
              <FinalModelCel
                rowItems={record}
                allTableData={transformedClientData}
              />
            ),
            props: { rowSpan: 0 },
          };
        }
        const rowSpan = dataSource.filter(
          (item) => item.clientName === record.clientName
        ).length;
        return {
          children: (
            <FinalModelCel
              rowItems={record}
              allTableData={transformedClientData}
            />
          ),
          props: { rowSpan },
        };
      },

      key: "qc3dsaModel_dsadsQc",
      // colSpan: 2,
    },

    // {
    //   title: null,
    //   render: (text, record, index) => {
    //     const prevRow = dataSource[index - 1];
    //     if (prevRow && prevRow.clientName === record.clientName) {
    //       return {
    //         children: <FinalModelDownloadCell rowItems={record} />,
    //         props: { rowSpan: 0 },
    //       };
    //     }
    //     const rowSpan = dataSource.filter(
    //       (item) => item.clientName === record.clientName
    //     ).length;
    //     return {
    //       children: <FinalModelDownloadCell rowItems={record} />,
    //       props: { rowSpan },
    //     };
    //   },
    //   key: "dsadsaempty",
    //   colSpan: 0,
    // },
    // ======
    // {
    //   title: (
    //     <FinalEda
    //       scriptPresentData={scriptPresentData}
    //       scriptPresentDataIsLoading={scriptPresentDataIsLoading}
    //     />
    //   ),
    //   className: currentRole == "Client_Manager" ? "hidden" : "",
    //   render: (data, record, index) => {
    //     if (index === 0) {
    //       return {
    //         children: (
    //           <FinalEdaCel rowItems={record} infraFormData={infraFormData} />
    //         ),
    //         props: { rowSpan: dataSource.length },
    //       };
    //     }
    //     return {
    //       props: { rowSpan: 0 },
    //     };
    //   },
    //   key: "qc3dsa",
    // },
  ];
  // Extract all siteManager emails
  // const siteManagerEmails = dataSource
  //   ?.map((item) => item.siteManager?.email)
  //   .filter(Boolean);

  return (
    <>
      {/* <pre>{JSON.stringify(currentProjectGitConnectionInfo, null, 4)}</pre> */}
      {/* {JSON.stringify(siteManagerEmails)} */}
      {/* {JSON.stringify(projectInfoData)}=== */}
      {/* ClientData== {JSON.stringify(infraFormData)}
      ClientData02== {JSON.stringify(transformedClientData)}
      ClientData02== {JSON.stringify(transformedClientData)} */}
      {/* <br></br>
      FileList======== {JSON.stringify(data)}
      <br></br>
      Infra======== {JSON.stringify(clientProgressData)}
      <pre>{JSON.stringify(updatedClientData, null, 4)}</pre> */}
      {/* {JSON.stringify(dataSource)} */}
      {/* {JSON.stringify(dataSource)}
      ===================== */}
      {/* Infra======== {JSON.stringify(clientProgressData)} */}
      {/* ClientData== {JSON.stringify(infraFormData)} */}
      {/* {isFetching ? "Loading...." : "fine"} */}
      <Table
        className="hed-center"
        // scroll={{ x: 600, y: 600 }}
        scroll={{ x: 600 }}
        // pagination={{ pageSize: 10 }}
        loading={isClientDataIsLoading}
        pagination={false}
        dataSource={dataSource}
        columns={columns}
        bordered
      />
    </>
  );
};

export default DataStatusTable;
